import * as React from 'react'
import Button from '@mui/lab/LoadingButton'
import CloseIcon from '@mui/icons-material/Close'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material'
import { useSnackbars } from '@assembly/hooks'
import { SnackbarType } from '@assembly/contexts'
import SignatureCanvas from 'react-signature-canvas'
import { useTheme } from '@mui/material/styles'
import { signContract } from '@assembly/api/contractor'
import { useAppStore } from '@assembly/hooks'
import jsPDF from 'jspdf'
import logo from '@assembly/assets/logo.png'

const transitionDuration: number = 200

interface SignContractDialogTitleProps {
  children?: React.ReactNode
  onClose: () => void
}

function SignContractDialogTitle(props: SignContractDialogTitleProps) {
  const { children, onClose, ...other } = props

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  )
}

interface SignContractDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: (data: any) => void
}

export default function SignContractDialog(props: SignContractDialogProps) {
  const { open, onClose, onSuccess } = props

  const [isSaving, setIsSaving] = React.useState<boolean>(false)
  const { addAlert } = useSnackbars()
  const signatureRef = React.useRef<SignatureCanvas>(null)
  const [isComponentMounted, setIsComponentMounted] = React.useState(false)
  const theme = useTheme()
  const { store } = useAppStore()

  // Initialize the component when the dialog opens
  React.useEffect(() => {
    if (open) {
      // Short delay to ensure DOM is ready
      const timer = setTimeout(() => {
        setIsComponentMounted(true);
      }, 100);

      return () => {
        clearTimeout(timer);
      };
    } else {
      setIsComponentMounted(false);
    }
  }, [open]);

  const handleClose = () => {
    setTimeout(() => { }, transitionDuration)
    setIsSaving(false)
    onClose()
  }

  const handleClickClear = () => {
    signatureRef.current?.clear()
  }

  const generatePDFBlob = async (): Promise<Blob> => {
    // Get signature as base64
    if (!signatureRef.current) {
      throw new Error('Signature reference is not available')
    }

    const signatureDataUrl = signatureRef.current.toDataURL('image/png')

    // Create a new PDF
    const report = new jsPDF('portrait', 'pt', 'a3')

    // Signature Image Loading from base64
    const signatureImg = new Image()
    signatureImg.src = signatureDataUrl
    signatureImg.style.width = '100px'
    signatureImg.style.height = 'auto'

    // Creating main div
    const el = document.createElement('div')
    el.innerHTML = store.contract
    el.style.letterSpacing = '1px'
    el.style.width = '720px'

    // Creating signature div containing label and image
    const signatureBox = document.createElement('div')
    signatureBox.style.width = '100%'
    signatureBox.style.display = 'flex'
    signatureBox.style.justifyContent = 'flex-end'
    signatureBox.style.paddingRight = '250px'

    // Creating inner div for signature
    const signatureInnerBox = document.createElement('div')
    signatureInnerBox.style.display = 'flex'

    // Appending image and label to inner div
    signatureInnerBox.appendChild(signatureImg)

    // Appending inner div to signature div
    signatureBox.appendChild(signatureInnerBox)

    // Appending signature div to main div
    el.appendChild(signatureBox)

    const img = document.createElement('img')
    img.src = logo

    report.addImage(img as any, 'PNG', 60, 30, 120, 33)

    // Return a promise that resolves with the PDF blob
    return new Promise((resolve) => {
      report.html(el as any, {
        x: 0,
        y: 0,
        margin: [60, 60, 78, 60],
        autoPaging: 'text',
        callback: function (pdf) {
          // Get the PDF as a blob
          const pdfBlob = pdf.output('blob')
          resolve(pdfBlob)
        },
      })
    })
  }

  const handleClickDone = async () => {
    if (signatureRef.current?.isEmpty()) {
      addAlert({
        message: 'Please provide a signature',
        type: SnackbarType.Error,
      })
      return
    }

    try {
      setIsSaving(true)

      // Generate the full PDF with signature
      const pdfBlob = await generatePDFBlob()
      const pdfFile = new File([pdfBlob], 'contract.pdf', { type: 'application/pdf' })

      // Create FormData to send only the PDF file
      const payload = new FormData()
      payload.append('thumbnail', 'false')
      payload.append('file', pdfFile) // Send only the PDF file with embedded signature

      const { data } = await signContract(payload)
      setIsSaving(false)
      onSuccess(data)
      handleClose()
      addAlert({
        message: 'Contract signed successfully.',
        type: SnackbarType.Success,
      })
    } catch (error: any) {
      setIsSaving(false)
      if (
        error.response &&
        error.response.data &&
        error.response.data.message &&
        typeof error.response.data.message === 'string'
      ) {
        addAlert({
          message: error.response.data.message,
          type: SnackbarType.Error,
        })
      } else {
        addAlert({
          message: 'An error occurred while signing the contract.',
          type: SnackbarType.Error,
        })
      }
    }
  }

  return (
    <div>
      <Dialog
        onClose={handleClose}
        open={open}
        maxWidth="sm"
        fullWidth
        transitionDuration={transitionDuration}
      >
        <SignContractDialogTitle onClose={handleClose}>
          Signature
        </SignContractDialogTitle>
        <DialogContent dividers={true}>
          {isComponentMounted && (
            <SignatureCanvas
              ref={signatureRef as any}
              penColor={theme.palette.secondary.main}
              canvasProps={{
                width: 550,
                height: 200,
                className: "signature-canvas",
                style: {
                  border: '1px solid #000',
                  borderRadius: '10px',
                  margin: '10px auto',
                  display: 'block'
                }
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClickClear}>Clear</Button>
          <Button loading={isSaving} onClick={handleClickDone}>
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
